import {
  Exclude,
  Expose,
  instanceToPlain,
  plainToInstance,
} from 'class-transformer';
import * as t from 'io-ts';
import { validateJSON, validateJSONNow } from '../web/validation';
import { Entity } from './entity';
import {
  notificationChannel,
  NotificationChannel,
} from './notification-channel';

export const NotificationSubscriptionRecipientCodec = t.partial({
  personId: t.string,
  notificationEndpointId: t.string,
});
export type NotificationSubscriptionRecipient = t.TypeOf<typeof NotificationSubscriptionRecipientCodec>;

export const NotificationSubscriptionPreferencesCodec = t.type({
  email: t.boolean,
  push: t.boolean,
  sms: t.boolean,
  enabled: t.boolean,
});
export type NotificationSubscriptionPreferences = t.TypeOf<typeof NotificationSubscriptionPreferencesCodec>;

// Parent-independent response shared by normal and managed subscription preference saves.
export const ResolvedNotificationSubscriptionCodec = t.intersection([
  t.type({ id: t.string, adminManaged: t.boolean }),
  NotificationSubscriptionRecipientCodec,
  NotificationSubscriptionPreferencesCodec,
]);
export type ResolvedNotificationSubscription = t.TypeOf<typeof ResolvedNotificationSubscriptionCodec>;

export const NotificationSubscriptionCodec = t.intersection([
  ResolvedNotificationSubscriptionCodec,
  t.type({
    notificationTemplateId: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    filters: t.string, // JSON
    createdById: t.string,
    lastModifiedById: t.string,
  }),
]);

export type NotificationSubscription = t.TypeOf<
  typeof NotificationSubscriptionCodec
>;

@Exclude()
export class NotificationSubscriptionEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  notificationTemplateId!: string;

  @Expose()
  email!: boolean;

  @Expose()
  push!: boolean;

  @Expose()
  sms!: boolean;

  @Expose()
  enabled!: boolean;

  @Expose()
  adminManaged!: boolean;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  // ─────────────────────────────
  // Optional fields
  // ─────────────────────────────

  @Expose()
  filters?: unknown;

  @Expose()
  personId?: string;

  @Expose()
  notificationEndpointId?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  readonly entityType = 'NOTIF_SUB';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for NotificationSubscriptionEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: NotificationSubscription): NotificationSubscriptionEntity {
    return plainToInstance(NotificationSubscriptionEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<NotificationSubscription> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(NotificationSubscriptionCodec, object);
  }

  asTypeValue(): NotificationSubscription {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(NotificationSubscriptionCodec, object);
  }

  get channels(): NotificationChannel[] {
    const channels: NotificationChannel[] = [];
    if (this.email) {
      channels.push(notificationChannel.email);
    }
    if (this.push) {
      channels.push(notificationChannel.push);
    }
    if (this.sms) {
      channels.push(notificationChannel.sms);
    }
    return channels;
  }
}

export const UserNotificationSubscriptionCodec = t.intersection([
  NotificationSubscriptionPreferencesCodec,
  t.type({
    notificationTemplateId: t.string,
    notificationTemplateName: t.string,
    notificationTemplateDescription: t.string,
    channels: t.array(t.string),
    adminManaged: t.boolean,
  }),
  t.partial({
    id: t.string,
    filters: t.string, // JSON
    notificationTemplateGroup: t.string,
    notificationTemplateSortOrder: t.number,
    canEnable: t.boolean,
    canDisable: t.boolean,
    subscriptionKind: t.union([t.literal('STANDARD'), t.literal('MANAGED_REPORT')]),
  }),
]);

export type UserNotificationSubscription = t.TypeOf<
  typeof UserNotificationSubscriptionCodec
>;

export const UserNotificationSaveCodec = t.intersection([
  t.type({}),
  t.partial({
    ...NotificationSubscriptionPreferencesCodec.props,
    id: t.string,
    filters: t.string, // JSON
    adminManaged: t.boolean,
    personId: t.string,
  }),
]);

export type UserNotificationSave = t.TypeOf<typeof UserNotificationSaveCodec>;
